import { match } from 'ts-pattern';

import { cn } from '@infinex/ui-styles';
import {
  ContextualHelp,
  Content,
  SkeletonText,
  Text,
  XStack,
} from '@infinex/ui/components';

import { useGasActivities } from '../../api/gas/useGasActivities';
import { useGasBalance } from '../../api/gas/useGasBalance';
import { type GasTopUpResult } from '../../hooks/gas/useGasTopUp';
import type { GasEstimate } from '../../types/move';
import { FormattedPrice } from '../formatted-number/FormattedPrice';

interface GasEstimateDisplayProps {
  gasEstimate: GasEstimate | null;
  isLoading: boolean;
  label?: string;
  gasTopUpResult: GasTopUpResult | null;
  isExternalSigner?: boolean;
  /**
   * When true and there is no estimate, render an invisible cost row so
   * Propose mode keeps the same vertical space as Execute / loading.
   */
  reserveSpace?: boolean;
}

export function GasTooltip({
  gasEstimate,
  isExternalSigner = false,
}: {
  gasEstimate: GasEstimate | null;
  isExternalSigner?: boolean;
}) {
  const { data: feeAccountBalance, status } = useGasBalance();
  if (!gasEstimate) return null;

  // External signers or user-paid gas transfers pay directly from their wallet
  const paysOnChain = isExternalSigner || gasEstimate.isGasPaidByUser;

  if (paysOnChain) {
    return (
      <ContextualHelp
        variant="info"
        placement="top"
        className="text-secondary"
        size="sm"
      >
        <Content className="w-64 text-sm">
          Network costs are paid directly from your wallet using the native
          token on this chain ({gasEstimate.breakdown.nativeSymbol}).
        </Content>
      </ContextualHelp>
    );
  }

  return (
    <ContextualHelp
      variant="info"
      placement="top"
      className="text-secondary"
      size="sm"
    >
      <Content className="w-64 text-sm">
        {gasEstimate.isSponsored && (
          <span>
            Some transactions are free for our valued Patrons.
            <br />
            <br />
          </span>
        )}
        Network costs are paid from your Gas Account. You have{' '}
        {status === 'loading' ? (
          <SkeletonText size="base" width={0.2} />
        ) : (
          <FormattedPrice value={feeAccountBalance} prefixDollarSign />
        )}
        credit.
        <br />
        <br />
        We automatically top up your Gas Account when it gets low.
      </Content>
    </ContextualHelp>
  );
}

export function GasNetworkCost({
  gasEstimate,
  gasTopUpResult,
  isExternalSigner = false,
}: {
  gasEstimate: GasEstimate | null;
  gasTopUpResult: GasTopUpResult | null;
  isExternalSigner?: boolean;
}) {
  const { data: gasBalance } = useGasBalance();
  const { data: gasActivitiesData } = useGasActivities();

  if (!gasEstimate) return null;

  const hasGasHistory = (gasActivitiesData?.activities?.length ?? 0) > 0;
  const isFirstTopUp = !hasGasHistory && gasBalance === 0;

  // External signers or user-paid gas transfers pay directly from their wallet
  const paysOnChain = isExternalSigner || gasEstimate.isGasPaidByUser;

  // User-paid-on-chain always shows regular secondary color (no top-up logic)
  const textColour = paysOnChain
    ? 'text-secondary'
    : match(gasTopUpResult?.canTopUpStatus)
        .with('topup_not_required', undefined, () => 'text-secondary')
        .with('topup_available', () =>
          isFirstTopUp ? 'text-brand' : 'text-caution'
        )
        .with(
          'topup_available_with_reduced_transfer',
          'deposit_required_for_topup',
          () => 'text-critical'
        )
        .exhaustive();

  return (
    <div>
      <Text className={cn('tabular-nums', 'text-sm font-semibold', textColour)}>
        <FormattedPrice
          value={parseFloat(gasEstimate.totalGasUSD)}
          prefixDollarSign
          strikeThrough={gasEstimate.isSponsored && !paysOnChain}
        />
      </Text>

      {gasEstimate.isSponsored && !paysOnChain && (
        <>
          {' '}
          <Text className="text-positive text-sm font-semibold">
            Free for Patrons
          </Text>
        </>
      )}
    </div>
  );
}

/**
 * Displays gas estimate for transfer operations (move/send).
 * Shows skeleton while loading. When `reserveSpace` is set and there is no
 * estimate (e.g. Propose mode), keeps an empty row so the quote strip does not
 * jump when mode settles.
 */
export function GasEstimateDisplay({
  gasEstimate,
  isLoading,
  label = 'Network cost',
  gasTopUpResult,
  isExternalSigner = false,
  reserveSpace = false,
}: GasEstimateDisplayProps) {
  if (isLoading) {
    return (
      <XStack justify={'space-between'} align="center" gap={'2px'}>
        <Text className="text-secondary text-sm">{label}</Text>
        <SkeletonText size="sm" width={0.4} />
      </XStack>
    );
  }

  if (!gasEstimate) {
    if (!reserveSpace) return null;
    return (
      <XStack
        justify={'space-between'}
        align="center"
        gap={'2px'}
        className="invisible"
        aria-hidden
      >
        <Text className="text-secondary text-sm">{label}</Text>
        <Text className="text-sm">$0.00</Text>
      </XStack>
    );
  }

  return (
    <XStack justify={'space-between'} align="center" gap={'2px'}>
      <Text className="text-secondary text-sm">
        {label}{' '}
        <GasTooltip
          gasEstimate={gasEstimate}
          isExternalSigner={isExternalSigner}
        />
      </Text>
      <GasNetworkCost
        gasEstimate={gasEstimate}
        gasTopUpResult={gasTopUpResult}
        isExternalSigner={isExternalSigner}
      />
    </XStack>
  );
}

export function getGasSubmitText(
  label: string,
  { canTopUpStatus }: Partial<Pick<GasTopUpResult, 'canTopUpStatus'>>
) {
  const labelWithTopUp = `${label} & top up`;
  if (!canTopUpStatus || canTopUpStatus === 'topup_not_required') return label;
  return labelWithTopUp;
}
